import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import '../../Assets/styles.css';
import toast from "react-hot-toast";
import { get } from "../../utility/fetchWeb";
import AllNotification from "./AllNotification";
import Spinner from "../Spinner";
import { NotificationsContext } from "../../contexts/NotificationsContext";

const NotificationPage = () => {
    const [activeFilter, setActiveFilter] = useState("All");
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();
    const {
        notifications,
        loading,
        getNotifications,
        markAsRead
    } = useContext(NotificationsContext);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
        getNotifications(filter);
    };

    const handleNotificationClick = (notification) => {
        // Mark as read first
        markAsRead(notification.id);

        // Navigate based on notification type
        switch (notification.type?.toLowerCase()) {
            case 'article':
                // Use referenceId to navigate to specific article details
                if (notification.referenceId) {
                    // Ensure referenceId is a valid number
                    const articleId = parseInt(notification.referenceId);
                    if (!isNaN(articleId)) {
                        navigate(`/article-details/${articleId}`);
                    } else {
                        console.error('Invalid article ID:', notification.referenceId);
                        navigate('/landing');
                    }
                } else {
                    // Fallback to general articles/landing page
                    navigate('/landing');
                }
                break;
            case 'community':
                console.log("Notification title:", notification.title); // for debug
                console.log("Reference ID:", notification.referenceId); // for debug
                
                const match = notification.title?.match(/\(Group:\s*(\d+)\)/i);

                if (match) {
                    const groupId = parseInt(match[1], 10);
                    console.log('Group ID extracted:', groupId);

                    // Navigate with both group ID and post reference ID for highlighting
                    const navigationState = {
                        groupId: groupId,
                        activeTab: 2 // Switch to Conversations tab
                    };

                    // If there's a referenceId, use it to highlight the specific post
                    if (notification.referenceId) {
                        navigationState.highlightPostId = notification.referenceId;
                    }

                    navigate('/groups-page', {
                        state: navigationState
                    });
                } else {
                    console.warn("Group ID not found in title:", notification.title);
                    navigate('/groups-page');
                }
                break;

            case 'connecthealth chat': {
                const currentUserType = sessionStorage.getItem("userType") || 2; // Default to 2 if not set
                const rType = Number(notification.userType2);
                if (rType === 1 || rType === 2) {
                    navigate(`/chat?rrt=${notification.userId2}&s=${currentUserType}&r=${rType}`);
                } else {
                    toast.error('No chat found');
                }
                break;
            }
            case 'appointment':
                // You might want to navigate to appointments page or specific appointment
                navigate('/appointments');
                break;
            default:
                // For other types, just mark as read without navigation
                console.log('Unknown notification type:', notification.type);
                break;
        }
    };

    // Helper function to clean notification titles for display
    const cleanNotificationTitle = (title) => {
        if (!title) return '';
        // Remove the "(Group: X)" part from the title for display
        return title.replace(/\s*\(Group:\s*\d+\)/, '');
    };

    useEffect(() => {
        getNotifications(activeFilter);
    }, []);

    // Filter notifications based on search term and active filter
    const filteredNotifications = notifications.filter(notification => {
        // First filter by search term - use cleaned title for searching
        const cleanTitle = cleanNotificationTitle(notification.title);
        const matchesSearch = cleanTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            notification.message?.toLowerCase().includes(searchTerm.toLowerCase());

        // Then filter by type if not "All"
        if (activeFilter === "All") {
            return matchesSearch;
        } else if (activeFilter === "Article") {
            // For articles, we might want to handle differently since they come from a different endpoint
            return matchesSearch && notification.type?.toLowerCase() === "article";
        } else {
            return matchesSearch && notification.type?.toLowerCase() === activeFilter.toLowerCase();
        }
    });

    return (
        <div className="w-100 m-t-40">
            <div className="col-4 m-l-40">
                <h1 className="m-b-20">Notifications</h1>
                <div className="filter-buttons">
                    {['All', 'Appointment', 'Community', 'Article'].map((filter) => (
                        <button
                            key={filter}
                            className={activeFilter === filter ? "active" : ""}
                            onClick={() => handleFilterChange(filter)}
                        >
                            {filter}
                        </button>
                    ))}
                </div>
            </div>
            {loading ? (
                <div><Spinner /></div>
            ) : filteredNotifications?.length === 0 ? (
                <div className="no-notifications">
                    <p>No notifications found</p>
                </div>
            ) : (
                <AllNotification
                    articles={filteredNotifications.map(notification => ({
                        ...notification,
                        displayTitle: cleanNotificationTitle(notification.title) // Keep original title intact for logic
                    }))}
                    onNotificationClick={handleNotificationClick}
                />
            )}
        </div>
    );
};

export default NotificationPage;
